/**
 * Open Dashboard API client (specs/009) — thin typed wrappers over the
 * public, stateless `/sat/open/*` surface. See
 * backend/src/main/java/com/tutortalk/sat/open/dto/*.java and
 * backend/src/main/java/com/tutortalk/sat/open/controller/ for the source of
 * truth these types mirror.
 *
 * Every call here (except `importProgress`) is anonymous — no cookies, no
 * X-User-Id header needed, and a 401 from any of them must never trigger the
 * refresh/redirect dance (see `client.ts`'s `PUBLIC_PATHS`, which includes
 * `/sat/open/`).
 */
import apiClient from './client';
import type { OpenAnswerRecord } from '../lib/satOpenProgress';
import type { SatStudentProfileOverviewDto } from './sat';

// ---------- Types ----------
// Mirrors com.tutortalk.sat.open.dto.SatOpenQuestionDto — the only question
// shape any open endpoint may return (no correctAnswer/explanation mid-play).
export interface SatOpenQuestion {
  questionId: string;
  sortOrder: number;
  questionText: string;
  answerA: string;
  answerB: string;
  answerC: string;
  answerD: string;
  section: string;
  domain: string;
  difficulty: string;
  skillName: string | null;
}

// Mirrors SatOpenDiagnosticFormResponse.
export interface SatOpenDiagnosticForm {
  templateId: string;
  templateName: string;
  questionCount: number;
  timeLimitMin: number | null;
  questions: SatOpenQuestion[];
}

// Mirrors SatOpenCheckRequest: exactly one of questionId / sourceCode.
export type SatOpenCheckRequest = { selectedKey: string } & (
  | { questionId: string; sourceCode?: never }
  | { sourceCode: string; questionId?: never }
);

// Mirrors SatOpenCheckResponse.
export interface SatOpenCheckResponse {
  correct: boolean;
  correctKey: string;
  explanation: string;
}

// Mirrors SatOpenDiagnosticResultDto (minus assignmentId — the open path
// creates no assignment row).
export interface SatOpenDiagnosticResult {
  templateId: string;
  templateName: string;
  overallScorePct: number;
  totalQuestions: number;
  totalCorrect: number;
  totalAnswered: number;
  completedAt: string;
  domainScores: { domain: string; attempted: number; correct: number; scorePct: number }[];
  questionResults: {
    questionId: string;
    questionText: string;
    answerA: string;
    answerB: string;
    answerC: string;
    answerD: string;
    correctAnswer: string;
    selectedAnswer: string | null;
    isCorrect: boolean;
    explanation: string;
    domain: string;
    skillName: string | null;
    timeSpentSec: number | null;
  }[];
}

/** Mirrors SatOpenAnswerItem. */
export interface SatOpenAnswerItem {
  questionId: string;
  selectedKey: string | null;
  answeredAt?: string;
  timeSpentSec?: number;
}

/** Mirrors SatOpenEvidenceItem. */
export interface SatOpenEvidenceItem {
  questionId: string;
  selectedKey: string | null;
  answeredAt: string;
  activity: 'DIAGNOSTIC' | 'PRACTICE';
}

/** Mirrors SatOpenPracticeCategory. */
export type SatOpenPracticeCategory = 'MATH' | 'READING' | 'GRAMMAR';

/** Mirrors SatOpenPracticeSetRequest: `category` and `domain` are mutually exclusive. */
export interface SatOpenPracticeSetRequest {
  domain?: string | null;
  category?: SatOpenPracticeCategory | null;
  /** 5..10; omitted = server default. */
  size?: number;
  excludeQuestionIds?: string[];
}

/** Mirrors SatOpenPracticeSetResponse. `domain` echoes a domain request, `category` a category request. */
export interface SatOpenPracticeSetResponse {
  domain: string | null;
  category?: SatOpenPracticeCategory | null;
  questions: SatOpenQuestion[];
}

// ---------- API ----------

export const satOpenApi = {
  /** GET the open Initial Diagnostic form — no auth, no request body. */
  getDiagnosticForm: async (): Promise<SatOpenDiagnosticForm> => {
    const response = await apiClient.get<SatOpenDiagnosticForm>('/sat/open/diagnostic-form');
    return response.data;
  },

  /**
   * Anonymous practice set. All fields optional. `category` (MATH | READING |
   * GRAMMAR) and `domain` are mutually exclusive (both = 400); `size` is 5..10
   * (server default when omitted; outside the bounds = 400). 409 = bank too small.
   */
  createPracticeSet: async (req: SatOpenPracticeSetRequest): Promise<SatOpenPracticeSetResponse> => {
    const response = await apiClient.post<SatOpenPracticeSetResponse>(
      '/sat/open/practice-set',
      req,
    );
    return response.data;
  },

  /**
   * LF3 "check my answer" reveal — grades one question in isolation. Identify the
   * question by exactly one of `questionId` or the stable `sourceCode` (bundled
   * marketing fixtures, whose UUIDs differ per environment).
   */
  check: async (req: SatOpenCheckRequest): Promise<SatOpenCheckResponse> => {
    const response = await apiClient.post<SatOpenCheckResponse>('/sat/open/check', req);
    return response.data;
  },

  /** Grades a whole anonymous diagnostic attempt in one shot. Stateless. */
  gradeDiagnostic: async (req: {
    answers: { questionId: string; selectedKey: string | null; answeredAt?: string; timeSpentSec?: number }[];
  }): Promise<SatOpenDiagnosticResult> => {
    const response = await apiClient.post<SatOpenDiagnosticResult>('/sat/open/diagnostic/grade', req);
    return response.data;
  },

  /**
   * Replays the whole anonymous learning history and returns the REAL
   * `SatStudentProfileOverviewDto` — the same DTO the authenticated
   * `/sat/me/profile/overview` returns, so the same frontend components
   * render both.
   */
  getOverview: async (req: {
    evidence: { questionId: string; selectedKey: string | null; answeredAt: string; activity: 'DIAGNOSTIC' | 'PRACTICE' }[];
    completedActivities: number;
  }): Promise<SatStudentProfileOverviewDto> => {
    const response = await apiClient.post<SatStudentProfileOverviewDto>('/sat/open/profile/overview', req);
    return response.data;
  },

  /**
   * The one authenticated call on this prefix (`@PreAuthorize("hasRole('STUDENT')")`
   * server-side) — hands the browser's accumulated anonymous progress to the
   * server at signup. Self-import only; there is no target-id parameter.
   */
  importProgress: async (req: {
    diagnostic: OpenAnswerRecord[];
    practice: OpenAnswerRecord[];
  }): Promise<{ diagnosticReplayed: boolean; importedPractice: number }> => {
    const response = await apiClient.post<{ diagnosticReplayed: boolean; importedPractice: number }>(
      '/sat/open/import',
      req,
    );
    return response.data;
  },
};
